import {Instagram} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLang } from "@/context/LanguageContext"

function PinterestIcon({ size = 16, strokeWidth = 1.5 }: { size?: number; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M8 22l4-5 4 5" />
      <circle cx="12" cy="10" r="8" />
    </svg>
  )
}

function TikTokIcon({ size = 16, strokeWidth = 1.5 }: { size?: number; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  )
}

function YouTubeIcon({ size = 16, strokeWidth = 1.5 }: { size?: number; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="3" />
      <path d="M10 9.5l5 2.5-5 2.5z" fill="currentColor" stroke="none" />
    </svg>
  )
}

export default function FooterSection() {
  const { t } = useLang()

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
  }

  return (
    <footer id="contact" className="relative" style={{ backgroundColor: "#131210" }}>
      {/* Footer content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <svg width="24" height="24" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="14" cy="14" r="13" stroke="rgba(248, 242, 235, 0.5)" strokeWidth="1.5" />
                <circle cx="14" cy="14" r="10" fill="rgba(248, 242, 235, 0.5)" />
              </svg>
              <span
                className="text-sm tracking-[4px] uppercase"
                style={{
                  fontFamily: "Segoe UI Light, Segoe UI, system-ui, sans-serif",
                  fontWeight: 300,
                  color: "rgba(248, 242, 235, 0.5)",
                }}
              >
                CLIPS'ON
              </span>
            </div>
            <p className="leading-relaxed max-w-sm mb-5" style={{ color: "rgba(248, 242, 235, 0.4)", fontSize: "15px" }}>
              {t(
                "Le bouton vestimentaire sans couture. Breveté en 1995.",
                "The seamless clothing button. Patented in 1995."
              )}
            </p>
            <div className="flex items-center gap-5">
              {[
                { icon: Instagram, url: "https://instagram.com/clipsonbutton", label: "Instagram" },
                { icon: PinterestIcon, url: "https://pinterest.com/clipsonbuttons", label: "Pinterest" },
              ].map(social => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="transition-colors"
                  style={{ color: "rgba(248, 242, 235, 0.3)" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
                  onMouseLeave={e => (e.currentTarget.style.color = "rgba(248, 242, 235, 0.3)")}
                >
                  <social.icon size={18} strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-[10px] tracking-[0.3em] uppercase mb-4" style={{ color: "rgba(248, 242, 235, 0.35)" }}>Navigation</h4>
            <div className="space-y-3">
              {[
                { label: t("Produit", "Product"), id: "savoir-faire" },
                { label: "Collections", id: "catalogue" },
                { label: "FAQ", id: "faq" },
                { label: "Contact", id: "contact" },
              ].map(link => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="block transition-colors"
                  style={{ fontSize: "14px", color: "rgba(248, 242, 235, 0.3)" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
                  onMouseLeave={e => (e.currentTarget.style.color = "rgba(248, 242, 235, 0.3)")}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[10px] tracking-[0.3em] uppercase mb-4" style={{ color: "rgba(248, 242, 235, 0.35)" }}>Contact</h4>
            <div className="space-y-3" style={{ color: "rgba(248, 242, 235, 0.3)", fontSize: "14px" }}>
              <p>info@clips-on.ch</p>
              <p>{t("", "")}<br />{t("Genève, Suisse", "1206 Geneva, Switzerland")}</p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderColor: "rgba(248, 242, 235, 0.06)" }}>
          <p className="text-[10px] tracking-wider" style={{ color: "rgba(248, 242, 235, 0.2)" }}>
            &copy; 2026 Clips'On. {t("Tous droits réservés.", "All rights reserved.")}
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/legal-notice"
              className="text-[10px] tracking-wider transition-colors"
              style={{ color: "rgba(248, 242, 235, 0.15)" }}
              onMouseEnter={e => (e.currentTarget.style.color = "rgba(248, 242, 235, 0.4)")}
              onMouseLeave={e => (e.currentTarget.style.color = "rgba(248, 242, 235, 0.15)")}
            >
              {t("Mentions légales", "Legal notice")}
            </Link>
            {[
              { label: t("CGV", "T&C"), to: "/terms-of-sale" },
              { label: t("Confidentialité", "Privacy"), to: "/privacy-policy" },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="text-[10px] tracking-wider transition-colors"
                style={{ color: "rgba(248, 242, 235, 0.15)" }}
                onMouseEnter={e => (e.currentTarget.style.color = "rgba(248, 242, 235, 0.4)")}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(248, 242, 235, 0.15)")}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}
